import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE } from '../config';
import {
    Activity,
    User,
    Calendar,
    Clock,
    Database,
    Globe,
    Info,
    ChevronLeft,
    ChevronRight,
    Search,
    X,
    ShieldCheck,
    ArrowRight
} from 'lucide-react';
import { format } from 'date-fns';

interface AuditLog {
    _id: string;
    adminId: string;
    adminEmail: string;
    action: string;
    targetModel: string;
    targetId: string;
    details: string;
    metadata: any;
    oldData?: any;
    newData?: any;
    ipAddress: string;
    userAgent: string;
    createdAt: string;
}

interface Pagination {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
}

const AuditLogs: React.FC = () => {
    const [logs, setLogs] = useState<AuditLog[]>([]);
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState<Pagination | null>(null);
    const [page, setPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterAction, setFilterAction] = useState('');
    const [filterModel, setFilterModel] = useState('');
    const [filterEmail, setFilterEmail] = useState('');
    const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);

    const fetchLogs = async (p: number) => {
        setLoading(true);
        try {
            const response = await axios.get(`${API_BASE}/api/audit-logs`, {
                params: {
                    page: p,
                    limit: 20,
                    search: searchTerm,
                    action: filterAction,
                    targetModel: filterModel,
                    adminEmail: filterEmail
                }
            });
            if (response.data.status === 'success') {
                setLogs(response.data.data.logs);
                setPagination(response.data.pagination);
            }
        } catch (error) {
            console.error("Error fetching audit logs:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            fetchLogs(1);
            setPage(1);
        }, 500);

        return () => clearTimeout(delayDebounceFn);
    }, [searchTerm, filterAction, filterModel, filterEmail]);

    useEffect(() => {
        fetchLogs(page);
    }, [page]);

    const getActionColor = (action: string) => {
        switch (action) {
            case 'CREATE': return 'bg-green-100 text-green-700 border-green-200';
            case 'UPDATE': return 'bg-blue-100 text-blue-700 border-blue-200';
            case 'DELETE': return 'bg-red-100 text-red-700 border-red-200';
            case 'LOGIN': return 'bg-purple-100 text-purple-700 border-purple-200';
            case 'LOGOUT': return 'bg-indigo-100 text-indigo-700 border-indigo-200';
            case 'SECURITY': return 'bg-orange-100 text-orange-700 border-orange-200';
            default: return 'bg-gray-100 text-gray-700 border-gray-200';
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                        <Activity className="text-[#523680]" />
                        System Audit Logs
                    </h1>
                    <p className="text-gray-500">Track all administrative actions across the platform</p>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                    <select
                        value={filterAction}
                        onChange={(e) => setFilterAction(e.target.value)}
                        className="px-3 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-[#523680]"
                    >
                        <option value="">All Actions</option>
                        <option value="CREATE">CREATE</option>
                        <option value="UPDATE">UPDATE</option>
                        <option value="DELETE">DELETE</option>
                        <option value="LOGIN">LOGIN</option>
                        <option value="LOGOUT">LOGOUT</option>
                        <option value="SECURITY">SECURITY</option>
                    </select>

                    <select
                        value={filterModel}
                        onChange={(e) => setFilterModel(e.target.value)}
                        className="px-3 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-[#523680]"
                    >
                        <option value="">All Categories</option>
                        <option value="User">User</option>
                        <option value="Admin">Admin</option>
                        <option value="AdminRole">Admin Role</option>
                        <option value="Blog">Blog</option>
                        <option value="Deposit">Deposit</option>
                        <option value="Withdraw">Withdraw</option>
                        <option value="KYC">KYC</option>
                        <option value="Wallet">Wallet</option>
                        <option value="AppVersion">App Version</option>
                        <option value="Popup">Popup</option>
                        <option value="Banner">Banner</option>
                        <option value="Notification">Notification</option>
                    </select>

                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <input
                            type="text"
                            placeholder="Search details or email..."
                            className="pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#523680] outline-none text-sm w-full md:w-64"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-xl shadow-xl shadow-gray-200/40 border border-gray-100 overflow-hidden">
                <div className="overflow-x-auto">
                    {/* Desktop Table */}
                    <table className="hidden md:table w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-gray-50 border-b border-gray-100">
                                <th className="px-6 py-4 text-sm font-black text-gray-400 uppercase tracking-widest">Admin</th>
                                <th className="px-6 py-4 text-sm font-black text-gray-400 uppercase tracking-widest">Action</th>
                                <th className="px-6 py-4 text-sm font-black text-gray-400 uppercase tracking-widest">Target</th>
                                <th className="px-6 py-4 text-sm font-black text-gray-400 uppercase tracking-widest">Details</th>
                                <th className="px-6 py-4 text-sm font-black text-gray-400 uppercase tracking-widest">Timestamp</th>
                                <th className="px-6 py-4 text-sm font-black text-gray-400 uppercase tracking-widest text-right">Info</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                            {loading ? (
                                Array.from({ length: 5 }).map((_, i) => (
                                    <tr key={i} className="animate-pulse">
                                        <td colSpan={6} className="px-6 py-4 h-16 bg-gray-50/50"></td>
                                    </tr>
                                ))
                            ) : logs.length > 0 ? (
                                logs.map((log) => (
                                    <tr key={log._id} className="hover:bg-gray-50/50 transition-colors">
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-2">
                                                <div className="w-8 h-8 rounded-full bg-purple-50 flex items-center justify-center text-[#523680] text-xs font-bold">
                                                    {(log.adminEmail || 'A').charAt(0).toUpperCase()}
                                                </div>
                                                <div className="flex flex-col">
                                                    <span className="text-sm font-medium text-gray-800">{(log.adminEmail || 'Admin').split('@')[0]}</span>
                                                    <span className="text-xs text-gray-500">{log.adminEmail || 'Unknown'}</span>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className={`px-2.5 py-1 rounded-full text-[10px] font-black border uppercase tracking-tighter ${getActionColor(log.action)}`}>
                                                {log.action}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-1.5 text-sm text-gray-600 font-medium">
                                                <Database className="w-4 h-4 text-gray-400" />
                                                {log.targetModel}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <p className="text-sm text-gray-600 max-w-xs truncate" title={log.details}>
                                                {log.details}
                                            </p>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex flex-col text-xs text-gray-500 font-bold">
                                                <span className="flex items-center gap-1 uppercase">
                                                    <Calendar className="w-3 h-3" />
                                                    {format(new Date(log.createdAt), 'MMM dd, yyyy')}
                                                </span>
                                                <span className="flex items-center gap-1 uppercase opacity-60">
                                                    <Clock className="w-3 h-3" />
                                                    {format(new Date(log.createdAt), 'hh:mm:ss a')}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <button
                                                className="p-2 text-gray-400 hover:text-[#523680] hover:bg-purple-50 rounded-lg transition-all"
                                                onClick={() => setSelectedLog(log)}
                                            >
                                                <Info className="w-5 h-5" />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={6} className="px-6 py-12 text-center text-gray-500 font-medium italic">
                                        No audit logs found.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>

                    {/* Mobile Card List */}
                    <div className="md:hidden divide-y divide-gray-100">
                        {loading ? (
                            Array.from({ length: 3 }).map((_, i) => (
                                <div key={i} className="p-4 space-y-3 animate-pulse">
                                    <div className="h-4 bg-gray-100 rounded w-1/3"></div>
                                    <div className="h-10 bg-gray-50 rounded w-full"></div>
                                </div>
                            ))
                        ) : logs.length > 0 ? (
                            logs.map((log) => (
                                <div key={log._id} className="p-4 hover:bg-gray-50/50 transition-colors" onClick={() => setSelectedLog(log)}>
                                    <div className="flex justify-between items-start mb-3">
                                        <div className="flex items-center gap-2">
                                            <div className="w-7 h-7 rounded-full bg-purple-50 flex items-center justify-center text-[#523680] text-[10px] font-black">
                                                {(log.adminEmail || 'A').charAt(0).toUpperCase()}
                                            </div>
                                            <div className="flex flex-col min-w-0">
                                                <span className="text-[11px] font-black text-gray-900 truncate uppercase tracking-tight">{(log.adminEmail || 'Admin').split('@')[0]}</span>
                                                <span className="text-[9px] text-gray-400 font-bold truncate lowercase">{log.adminEmail || 'Unknown'}</span>
                                            </div>
                                        </div>
                                        <span className={`px-2 py-0.5 rounded-full text-[9px] font-black border uppercase tracking-tighter ${getActionColor(log.action)}`}>
                                            {log.action}
                                        </span>
                                    </div>

                                    <div className="grid grid-cols-2 gap-3 p-2.5 bg-gray-50 rounded-xl border border-gray-100/50 mb-2">
                                        <div className="min-w-0">
                                            <span className="block text-[8px] text-gray-400 font-bold uppercase mb-0.5">Target</span>
                                            <div className="flex items-center gap-1 text-[10px] font-black text-gray-700 uppercase truncate">
                                                <Database className="w-3 h-3 text-gray-300" />
                                                {log.targetModel}
                                            </div>
                                        </div>
                                        <div>
                                            <span className="block text-[8px] text-gray-400 font-bold uppercase mb-0.5">Time</span>
                                            <div className="text-[9px] font-black text-gray-700 uppercase flex flex-col leading-none">
                                                <span>{format(new Date(log.createdAt), 'MMM dd')}</span>
                                                <span className="opacity-40">{format(new Date(log.createdAt), 'hh:mm a')}</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex items-center justify-between">
                                        <p className="text-[10px] text-gray-500 font-medium truncate italic pr-4">
                                            "{log.details}"
                                        </p>
                                        <Info className="w-4 h-4 text-purple-300 shrink-0" />
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="p-10 text-center text-gray-400 font-medium italic text-sm">
                                No audit logs found.
                            </div>
                        )}
                    </div>
                </div>

                {pagination && pagination.totalPages > 1 && (
                    <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
                        <span className="text-sm text-gray-600">
                            Showing {logs.length} of {pagination.total} entries
                        </span>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setPage(p => Math.max(1, p - 1))}
                                disabled={page === 1}
                                className="p-2 border border-gray-200 rounded-lg disabled:opacity-50 hover:bg-white"
                            >
                                <ChevronLeft className="w-4 h-4" />
                            </button>
                            <div className="flex items-center gap-1 px-2">
                                {[...Array(pagination.totalPages)].map((_, i) => (
                                    <button
                                        key={i + 1}
                                        onClick={() => setPage(i + 1)}
                                        className={`w-8 h-8 rounded-lg text-sm font-medium transition-all ${page === i + 1
                                            ? 'bg-[#523680] text-white shadow-md'
                                            : 'text-gray-600 hover:bg-white'
                                            }`}
                                    >
                                        {i + 1}
                                    </button>
                                )).slice(Math.max(0, page - 3), Math.min(pagination.totalPages, page + 2))}
                            </div>
                            <button
                                onClick={() => setPage(p => Math.min(pagination!.totalPages, p + 1))}
                                disabled={page === pagination.totalPages}
                                className="p-2 border border-gray-200 rounded-lg disabled:opacity-50 hover:bg-white"
                            >
                                <ChevronRight className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Detailed Info Modal */}
            {selectedLog && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden animate-in zoom-in-95 duration-200">
                        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-purple-100 rounded-lg text-[#523680]">
                                    <ShieldCheck className="w-5 h-5" />
                                </div>
                                <div>
                                    <h3 className="font-bold text-gray-900 border-none">Audit Log Details</h3>
                                    <p className="text-xs text-gray-500">Transaction ID: {selectedLog._id}</p>
                                </div>
                            </div>
                            <button
                                onClick={() => setSelectedLog(null)}
                                className="p-2 hover:bg-gray-200 rounded-full transition-colors"
                            >
                                <X className="w-5 h-5 text-gray-500" />
                            </button>
                        </div>

                        <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
                            {/* Summary Cards */}
                            <div className="grid grid-cols-2 gap-4">
                                <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Administrator</span>
                                    <p className="text-sm font-semibold text-gray-800 mt-1 truncate">{selectedLog.adminEmail}</p>
                                    <p className="text-[10px] text-gray-500 mt-0.5">ID: {selectedLog.adminId}</p>
                                </div>
                                <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Action Type</span>
                                    <div className="mt-1">
                                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${getActionColor(selectedLog.action)}`}>
                                            {selectedLog.action}
                                        </span>
                                    </div>
                                    <p className="text-[10px] text-gray-500 mt-1.5">Model: {selectedLog.targetModel}</p>
                                </div>
                            </div>

                            {/* Main Details */}
                            <div>
                                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Description</h4>
                                <div className="p-4 bg-[#523680]/5 rounded-xl border border-[#523680]/10 text-gray-700 text-sm leading-relaxed">
                                    {selectedLog.details}
                                </div>
                            </div>

                            {/* Technical Meta */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Network Info</h4>
                                    <div className="space-y-2 text-sm">
                                        <div className="flex items-center gap-2 text-gray-600">
                                            <Globe className="w-4 h-4 text-[#523680]" />
                                            <span className="font-mono text-xs bg-gray-100 px-2 py-0.5 rounded border border-gray-200">
                                                IP: {selectedLog.ipAddress || '127.0.0.1 (Local)'}
                                            </span>
                                        </div>
                                        <div className="flex items-start gap-2 text-gray-600">
                                            <Activity className="w-4 h-4 mt-0.5" />
                                            <span className="text-xs break-all">{selectedLog.userAgent}</span>
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Timing</h4>
                                    <div className="space-y-2 text-sm">
                                        <div className="flex items-center gap-2 text-gray-600">
                                            <Calendar className="w-4 h-4" />
                                            <span>{format(new Date(selectedLog.createdAt), 'MMMM dd, yyyy')}</span>
                                        </div>
                                        <div className="flex items-center gap-2 text-gray-600">
                                            <Clock className="w-4 h-4" />
                                            <span>{format(new Date(selectedLog.createdAt), 'hh:mm:ss a')}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Data Changes: Before vs After */}
                            {(selectedLog.oldData || selectedLog.newData) && (
                                <div className="space-y-3">
                                    <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">State Comparison (Before vs After)</h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <span className="text-[10px] font-bold text-red-500 uppercase px-2 py-0.5 bg-red-50 rounded">Previous State</span>
                                            <pre className="p-3 bg-gray-50 rounded-xl text-[10px] text-gray-600 border border-gray-100 overflow-x-auto max-h-48 scrollbar-hide">
                                                {selectedLog.oldData ? JSON.stringify(selectedLog.oldData, null, 2) : 'No previous data recorded'}
                                            </pre>
                                        </div>
                                        <div className="space-y-2 relative">
                                            <div className="absolute -left-3 top-1/2 -translate-y-1/2 hidden md:block">
                                                <div className="bg-white rounded-full p-1 shadow-sm border border-gray-100 text-gray-400">
                                                    <ArrowRight className="w-3 h-3" />
                                                </div>
                                            </div>
                                            <span className="text-[10px] font-bold text-green-500 uppercase px-2 py-0.5 bg-green-50 rounded">New State</span>
                                            <pre className="p-3 bg-gray-900 rounded-xl text-[10px] text-green-400 overflow-x-auto max-h-48 scrollbar-hide">
                                                {selectedLog.newData ? JSON.stringify(selectedLog.newData, null, 2) : 'No new data recorded'}
                                            </pre>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* JSON Metadata */}
                            {selectedLog.metadata && Object.keys(selectedLog.metadata).length > 0 && (
                                <div>
                                    <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Attached Metadata (Payload)</h4>
                                    <pre className="p-4 bg-gray-900 rounded-xl text-xs text-blue-400 overflow-x-auto font-mono scrollbar-hide border border-blue-900/20">
                                        {JSON.stringify(selectedLog.metadata, null, 2)}
                                    </pre>
                                </div>
                            )}
                        </div>

                        <div className="p-4 bg-gray-50 border-t border-gray-100 flex justify-end px-6">
                            <button
                                onClick={() => setSelectedLog(null)}
                                className="px-6 py-2 bg-[#523680] text-white rounded-lg text-sm font-bold shadow-lg shadow-purple-200 hover:shadow-xl hover:-translate-y-0.5 transition-all"
                            >
                                Done
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AuditLogs;
